import { useMemo } from "react";
import type { Process } from "@archlang/scene/arch-model";
import {
  perStepLatency,
  buildLatencyEnv,
  type ResolvedModel,
  type ProcessModel,
  type WalkResult,
  type StepLatency,
} from "@archlang/engine";
import { processKey } from "../processKey.js";

/**
 * Per-step latencies for the active process, keyed by walk `stepIndex` — the
 * sequence-view ruler (cumulative elapsed) + per-hop arrow labels (own latency).
 * Priced over the SAME walk the view renders, so labels line up with rows. Pure
 * + memoized; empty map when there's no model / selection. A fold throw degrades
 * to an empty map.
 */
export function useProcessStepLatencies(
  resolvedModel: ResolvedModel | null,
  processModels: readonly ProcessModel[],
  activeProcessId: string | null,
  walkResult: WalkResult | null,
): ReadonlyMap<number, StepLatency> {
  return useMemo(() => {
    const empty = new Map<number, StepLatency>();
    if (!resolvedModel || !activeProcessId || !walkResult) return empty;
    const pm = processModels.find((p) => processKey(p as unknown as Process) === activeProcessId);
    if (!pm) return empty;
    try {
      const rows = perStepLatency(walkResult, buildLatencyEnv(resolvedModel, pm));
      return new Map(rows.map((r) => [r.stepIndex, r]));
    } catch {
      return empty;
    }
  }, [resolvedModel, processModels, activeProcessId, walkResult]);
}
